import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { USER_DEFAULT_FORMALISTE } from '@europrocurement/l2d-modules/modules/Utilisateur';
import InfoCardDossier from '../../InfoCardDossier/InfoCardDossier';
import { PeoplePreview } from '../../../liste';
import ConditionalMessage from './ConditionalMessage';
import { FormalisteCardProps } from './types';

const FormalisteCard: React.FunctionComponent<FormalisteCardProps> = function ({
    formalisteId,
    formalisteNom,
    formalistePrenom,
    formalisteUserIdentifier,
    availableFormalistes = [],
    onFormalisteChange,
    infoCardSx,
    textStyle,
    palette,
    userInterne,
    onClickUser,
}) {
    const hasAssignedFormaliste =
        Boolean(formalistePrenom && formalisteNom) && formalisteId !== USER_DEFAULT_FORMALISTE;

    const isAssignedFormalisteInAvailableList = () => {
        if (!hasAssignedFormaliste) return false;

        return availableFormalistes.some((formaliste) => {
            const idMatch =
                formalisteUserIdentifier &&
                formaliste.id?.toString() === formalisteUserIdentifier?.toString();
            const nameMatch =
                formaliste.prenom === formalistePrenom && formaliste.nom === formalisteNom;

            return idMatch || nameMatch;
        });
    };

    const shouldEnableFormalisteChange = () => {
        if (!onFormalisteChange || availableFormalistes.length === 0) return false;

        if (!hasAssignedFormaliste && availableFormalistes.length > 0) return true;

        const formalisteInList = isAssignedFormalisteInAvailableList();

        if (hasAssignedFormaliste && !formalisteInList) return true;
        if (hasAssignedFormaliste && formalisteInList && availableFormalistes.length === 1)
            return false;
        if (hasAssignedFormaliste && formalisteInList && availableFormalistes.length > 1)
            return true;

        return false;
    };

    const getFormalisteChangeTooltip = (): string => {
        if (!userInterne) return '';

        if (availableFormalistes.length === 0) {
            return 'Aucun formaliste disponible';
        }

        const formalisteInList = isAssignedFormalisteInAvailableList();
        if (hasAssignedFormaliste && formalisteInList && availableFormalistes.length === 1) {
            return 'Aucun autre formaliste disponible';
        }

        return 'Modifier le formaliste assigné';
    };

    const canChangeFormaliste = shouldEnableFormalisteChange();
    const formalisteInList = isAssignedFormalisteInAvailableList();
    const formalisteNotInList = hasAssignedFormaliste && !formalisteInList;

    const renderContent = () => {
        if (hasAssignedFormaliste) {
            return (
                <>
                    <PeoplePreview
                        firstName={formalistePrenom}
                        lastName={formalisteNom}
                        userIdentifier={formalisteUserIdentifier}
                        sx={{ margin: '0 10px 0 0' }}
                        onClick={onClickUser}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            sx={textStyle}
                            component="span"
                        >
                            {`${formalistePrenom} ${formalisteNom}`}
                        </Typography>
                        {formalisteNotInList && (
                            <ConditionalMessage
                                text="Formaliste non répertorié"
                                type="warning"
                            />
                        )}
                    </Box>
                </>
            );
        }

        if (!hasAssignedFormaliste && availableFormalistes.length > 0 && userInterne) {
            return (
                <Typography
                    sx={{
                        ...textStyle,
                        fontStyle: 'italic',
                        color: palette.warning.main,
                    }}
                    component="span"
                >
                    Aucun formaliste assigné
                </Typography>
            );
        }

        return userInterne ? (
            <Typography
                sx={{
                    ...textStyle,
                    fontStyle: 'italic',
                    color: palette.error.main,
                }}
                component="span"
            >
                Aucun formaliste disponible
            </Typography>
        ) : null;
    };

    const cardContent = (
        <InfoCardDossier
            label="Formaliste attitré(e)"
            sx={infoCardSx}
            onClick={canChangeFormaliste ? onFormalisteChange : undefined}
            actionIcon={
                canChangeFormaliste ? (['fasl', 'pen'] as unknown as IconDefinition) : undefined
            }
        >
            {renderContent()}
        </InfoCardDossier>
    );

    const needsTooltip =
        !canChangeFormaliste &&
        userInterne &&
        (availableFormalistes.length === 0 ||
            (hasAssignedFormaliste && formalisteInList && availableFormalistes.length === 1));

    if (needsTooltip) {
        return (
            <Tooltip
                title={getFormalisteChangeTooltip()}
                arrow
                placement="top"
            >
                <Box>{cardContent}</Box>
            </Tooltip>
        );
    }

    return cardContent;
};

export default FormalisteCard;
